/**
 * Caption overlay renderer (#2233). Paints the currently-visible
 * caption segment(s) on top of the shared `EditorMediaSurface`.
 *
 * Extracted from the old `caption-media-player.tsx` so
 * `caption-editor/` no longer owns a media player — this file only
 * knows about caption segments and their position offsets.
 */

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useCaption } from '@utils/providers/caption-context.tsx';

interface Props {
  /** When true, use a tighter bottom margin (video is docked in split view). */
  hideControls?: boolean;
}

export function CaptionSegmentOverlay({ hideControls = false }: Props) {
  const { currentTime } = useAudio();
  const { document: myDocument } = useCaption();

  // Track which caption segment is currently visible. Only update when
  // the active segment actually changes — not on every 300ms progress
  // tick. This prevents AnimatePresence/motion.div from re-rendering
  // thousands of times during playback, which was a major source of
  // memory growth.
  const lastFilteredRef = useRef(
    myDocument?.document
      ?.filter(
        i => i.start <= currentTime * 1000 && i.stop >= currentTime * 1000
      )
      ?.slice(-1)
  );
  const [filteredItems, setFilteredItems] = useState(lastFilteredRef.current);

  useEffect(() => {
    const timeMs = currentTime * 1000;
    const next = myDocument?.document
      ?.filter(i => i.start <= timeMs && i.stop >= timeMs)
      ?.slice(-1);
    const prev = lastFilteredRef.current?.[0];
    const now = next?.[0];
    if (
      prev?.start !== now?.start ||
      prev?.stop !== now?.stop ||
      prev?.text !== now?.text ||
      prev?.color !== now?.color ||
      prev?.bold !== now?.bold ||
      prev?.italic !== now?.italic ||
      prev?.underlined !== now?.underlined ||
      prev?.strike !== now?.strike ||
      prev?.position !== now?.position ||
      prev?.fontFamily !== now?.fontFamily
    ) {
      lastFilteredRef.current = next;
      setFilteredItems(next);
    }
  }, [currentTime, myDocument?.document]);

  return (
    <AnimatePresence>
      {filteredItems?.map(item => {
        const pos = item.position;
        let positionPercent = 100;
        if (pos !== null && pos !== undefined) {
          const numPos = typeof pos === 'string' ? parseInt(pos, 10) : pos;
          if (!isNaN(numPos)) {
            positionPercent = Math.max(0, Math.min(100, numPos));
          }
        }
        const safePosition = hideControls
          ? 10 + (positionPercent / 100) * 75
          : 10 + (positionPercent / 100) * 80;

        return (
          <div
            key={item.start}
            className="absolute left-0 right-0 py-2 px-4 flex justify-center"
            style={{
              top: `${safePosition}%`,
              transform: 'translateY(-50%)'
            }}
          >
            <motion.div
              className="text-center flex items-center justify-center px-2 max-w-fit h-full overflow-auto rounded-2xl select-none"
              style={{
                color: item.color || 'white',
                backgroundColor: `rgba(0, 0, 0, 0.7)`,
                fontWeight: item.bold ? 'bold' : 'normal',
                textDecoration: `${item.underlined ? 'underline ' : ''}${
                  item.strike ? 'line-through' : ''
                }`,
                fontStyle: item.italic ? 'italic' : 'normal',
                fontFamily: item.fontFamily || undefined
              }}
              dangerouslySetInnerHTML={{
                __html: item.text.replace(/\n/g, '<br>')
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            />
          </div>
        );
      })}
    </AnimatePresence>
  );
}
